import React from 'react';
import { Box, Button, IconButton, LinearProgress, Typography, alpha, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUpload } from '../contexts/UploadContext';

// Keeps an upload visible (and stoppable) while the user is on another page.
// On the dashboard the upload card shows the same thing, so nothing is rendered there.
const UploadStatusBar: React.FC = () => {
  const { uploading, progress, results, summaryOpen, cancelUpload, openSummary, closeSummary } = useUpload();
  const { palette } = useTheme();
  const t = palette.tokens;
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === '/dashboard') return null;

  const barSx = {
    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 3, py: 1.25,
    bgcolor: alpha(t.primary, 0.08), borderBottom: `1px solid ${alpha(t.primary, 0.25)}`,
  } as const;

  if (uploading && progress) {
    return (
      <Box role="status" aria-live="polite" sx={barSx}>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '14px', color: t.ink, mb: 0.5 }}>
            Uploading file {progress.current} of {progress.total}: {progress.currentFile}
          </Typography>
          <LinearProgress variant="determinate" value={Math.round(((progress.current - 1) / progress.total) * 100)} sx={{ height: 5, borderRadius: 3 }} />
        </Box>
        <Button size="small" variant="outlined" color="error" onClick={cancelUpload} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Stop
        </Button>
      </Box>
    );
  }

  if (!uploading && results && !summaryOpen) {
    const stopped = results.processed < results.total;
    return (
      <Box role="status" aria-live="polite" sx={barSx}>
        <Typography sx={{ flex: '1 1 260px', fontSize: '14px', color: t.ink }}>
          {stopped
            ? `Upload stopped after ${results.processed} of ${results.total} files. The ones already read are ready to review.`
            : `Upload finished. ${results.total} file${results.total === 1 ? '' : 's'} ready to review.`}
        </Typography>
        <Button size="small" variant="contained" onClick={() => { openSummary(); navigate('/dashboard'); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Review
        </Button>
        <IconButton size="small" aria-label="Dismiss" onClick={closeSummary}><CloseIcon fontSize="small" /></IconButton>
      </Box>
    );
  }

  return null;
};

export default UploadStatusBar;
